import { Checkbox, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, useMediaQuery } from '@mui/material';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import MemoizedContactTableRow from './memoizedContactTableRow';
import dashboardStyles from './../../../assets/styles/componentStyles/callAnalysisStyles/callAnalysisDashboard.module.scss';
import style from '../../../assets/styles/componentStyles/campaignContactStyles/campaignContactStyle.module.scss';
import globalStyles from "../../../assets/styles/global.module.scss";
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import globalStrenameDocumentyles from "../../../assets/renameDocument.svg";
import { campaignContact, campaignContactFieldData, getCampaignContactResponse, globalMenuItemData } from '../../../models/axiosModel';
import deleteDoc from "../../../assets/deleteDoc.svg";
import { ContactContext, ContactContextPayload } from '../../../context/contactContext';
import { useGenericStore } from '../../../store/genericStore';
import { filtersInterface, sortConfig } from './contactList';
import { UserContext, UserContextPayload } from '../../../context/userContext';
import { useVirtualizer } from '@tanstack/react-virtual';
import { tertiaryColor } from '../../../utils/colors';
import { hasContactManageAccess } from '../../../utils/constants';
import { isArchivedContactList, isMasterContactList } from './contactConstant';
import GlobalMenu from '../../globalMenu';
import moment from 'moment';

interface ContactTableProps {
    selectContact: campaignContact[],
    setSelectContact: React.Dispatch<React.SetStateAction<campaignContact[]>>,
    editCampaignContact: (contacts: campaignContact[]) => void,
    deleteCampaignContact: (contacts: campaignContact[]) => void,
    setRequiredField: React.Dispatch<React.SetStateAction<campaignContactFieldData | null>>,
    setFlagEditFieldDialog: React.Dispatch<React.SetStateAction<boolean>>,
    filters: filtersInterface,
    sortConfig: sortConfig,
    setSortConfig: React.Dispatch<React.SetStateAction<sortConfig>>, 
    setFilteredContacts: React.Dispatch<React.SetStateAction<campaignContact[]>>,
    setShowDeleteFieldConfirmation: React.Dispatch<React.SetStateAction<boolean>>,
    searchQuery: string,
}

const ContactTable: React.FC<ContactTableProps> = (props) => {

    const { selectContact, setSelectContact, editCampaignContact, deleteCampaignContact, setRequiredField, setFlagEditFieldDialog, filters, sortConfig, setSortConfig, setFilteredContacts, setShowDeleteFieldConfirmation, searchQuery } = props;

    const { campaignContactData, filteredCampaignContactData, setFilteredCampaignContactData, campaignContactFieldData, campaignContactListData } = useContext(ContactContext) as ContactContextPayload;
    const globalStatusData = useGenericStore((state) => state.listOfChatbotStatus);
    const { userDetail, apiRoutesPermissionAssignment } = useContext(UserContext) as UserContextPayload;

    const breakPoints = {
        flagSmallScreen: useMediaQuery<boolean>('(min-width: 0px) and (max-width: 1000px)'),
    }

    const innerHeight = (window.innerHeight - 250);
    const tableContainerRef = useRef<HTMLDivElement>(null); 

    const isContactListMasterContactList = isMasterContactList(campaignContactData?.[0]?.contact_list_name);
    const isContactListArchived = isArchivedContactList(campaignContactData?.[0], globalStatusData);

    const menuItem: globalMenuItemData[] = [
        {
            type: "item",
            label: "Edit",
            icon: <img alt="" src={globalStrenameDocumentyles} />,
            isDisable: isContactListArchived,
            onClick: (contact: campaignContact) => {
                const contactArray: campaignContact[] = Array(1).fill(contact);
                editCampaignContact(contactArray);
            }
        },
        ...(!isContactListMasterContactList ? [
            {
                type: "item" as const,
                label: "Delete",
                icon: <img alt="" src={deleteDoc} />,
                isDisable: isContactListMasterContactList || isContactListArchived,
                onClick: (contact: campaignContact) => {
                    const contactArray: campaignContact[] = Array(1).fill(contact);
                    deleteCampaignContact(contactArray);
                }
            }
        ] : [])
    ]

    const menuFieldItem: globalMenuItemData[] = [
        {
            type: "item",
            label: "Edit",
            icon: <img alt="" src={globalStrenameDocumentyles} />,
            onClick: (field: campaignContactFieldData) => {
                setRequiredField(field);
                setFlagEditFieldDialog(true);
            },
            isDisable: isContactListArchived,
        },
        {
            type: "item",
            label: "Delete",
            icon: <img alt="" src={deleteDoc} />,
            onClick: (field: campaignContactFieldData) => {
                setRequiredField(field);
                setShowDeleteFieldConfirmation(true);
            },
            isDisable: isContactListArchived,
        }
    ]

    const [anchorEl, setAnchorEl] = useState<null | SVGSVGElement>(null);
    const [currentRowIndex, setCurrentRowIndex] = useState<number | null>(null);
    const [hoveredHeader, setHoveredHeader] = useState<number | null>(null);
    const [changeField, setChangeField] = useState<string>("");

    const handleMenuOpen = (event: React.MouseEvent<SVGSVGElement, MouseEvent>, rowIndex: number) => {
        setCurrentRowIndex(rowIndex);
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setCurrentRowIndex(null);
        setAnchorEl(null);
    };

    const handleSelectContact = (data: campaignContact) => {
        setSelectContact((prev) => {
            if (prev.find((item) => item.contact_id == data.contact_id)) {
                return prev.filter((item) => item.contact_id !== data.contact_id);
            } else {
                return [...prev, data];
            }
        })
    }

    const handleAllChanges = () => {
        if (filteredCampaignContactData) {
            if (selectContact.length === filteredCampaignContactData[0].contact.length) {
                setSelectContact([]);
            } else {
                setSelectContact(filteredCampaignContactData[0].contact.filter((contact) => !contact.flag_deleted));
            }
        }
    }

    const sortField = (field: campaignContactFieldData[]) => {
        return field.sort((a, b) => a.contact_field_id - b.contact_field_id);
    }

    const sortContacts = (key: string, direction: string, contactData: getCampaignContactResponse[] | null) => {
        if (contactData) {
            const filtered_contacts = contactData.map((item) => {
                const filter_contact = item.contact.sort((a, b) => {
                    let aValue, bValue;
                    if (key === "Created At") {
                        aValue = a.created_at_timestamp || "";
                        bValue = b.created_at_timestamp || "";
                    } else {
                        aValue = a.contact_data.find((it) => it.contact_field_name === key)?.contact_data_value || "";
                        bValue = b.contact_data.find((it) => it.contact_field_name === key)?.contact_data_value || "";
                    }
                    if (aValue === "" && bValue === "") {
                        return 0;
                    }
                    if (aValue === "") {
                        return direction === 'ascending' ? -1 : 1;
                    }
                    if (bValue === "") {
                        return direction === 'ascending' ? 1 : -1;
                    }
                    if (aValue < bValue) {
                        return direction === 'ascending' ? -1 : 1;
                    }
                    if (aValue > bValue) {
                        return direction === 'ascending' ? 1 : -1;
                    }
                    return 0;
                });
                return {
                    ...item,
                    contact: filter_contact,
                };
            });

            return filtered_contacts;
        } else return [];
    }

    const sortData = (key: string) => {
        setChangeField(key);
        let dir = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            dir = 'descending';
        }
        if (filteredCampaignContactData) {
            setSortConfig({ key: key, direction: dir });
        }
    }

    const getFilteredContacts = useMemo(() => {
        let contacts = campaignContactData?.[0]?.contact || [];
        if (sortConfig.key.length > 0 && sortConfig.direction.length > 0) { // Sort the Data.
            contacts = sortContacts(sortConfig.key, sortConfig.direction, campaignContactData)?.[0]?.contact || [];
        } 
        const query = searchQuery?.trim()?.toLowerCase() || "";
        const filtered_contacts = contacts.filter((contact) => {
            const contactFields = contact.contact_data || [];

            const firstName = contactFields.find(field => field.contact_field_name === "First Name")?.contact_data_value || "";
            const phoneNumber = contactFields.find(field => field.contact_field_name === "Phone Number")?.contact_data_value || "";
            const tags = contact.contact_tag_assignment.map((tag) => tag.contact_tag_name) || [];
            const deletionMatch = filters?.includeDeletedContacts || !contact.flag_deleted;
            const contactCreatedAt = contact?.created_at_timestamp ? moment(contact.created_at_timestamp) : null;

            const nameMatch = !filters.name?.length || filters.name.includes(firstName);
            const phoneMatch = !filters.phoneNumber?.length || filters.phoneNumber.includes(phoneNumber);
            const tagMatch = !filters.tags?.length || filters.tags.some((tag: any) => tags.includes(tag));

            const startDate = filters.startDate ? moment(filters.startDate)?.startOf("day") : null;
            const endDate = filters.endDate ? moment(filters.endDate)?.endOf("day") : null;
            let dateMatch = true;

            if (contactCreatedAt) {
                if (startDate && endDate) {
                    dateMatch = contactCreatedAt.isBetween(startDate, endDate, undefined, "[]");
                } else if (startDate) {
                    dateMatch = contactCreatedAt.isSameOrAfter(startDate);
                } else if (endDate) {
                    dateMatch = contactCreatedAt.isSameOrBefore(endDate);
                }
            }

            const searchMatch = !query ||
                contactFields.some((data) => data.contact_data_value?.toLowerCase()?.includes(query)) ||
                contact.contact_tag_assignment?.some((tag) => tag.contact_tag_name?.toLowerCase()?.includes(query));

            return nameMatch && phoneMatch && tagMatch && deletionMatch && dateMatch && searchMatch;
            // && contact.flag_deleted == includeDeletedContacts;
        });
        setFilteredContacts(filtered_contacts);
        return filtered_contacts;
    }, [campaignContactData, sortConfig, filters, searchQuery]);

    const virtualizer = useVirtualizer({
        count: getFilteredContacts.length,
        getScrollElement: () => tableContainerRef.current,
        estimateSize: () => 48,
        overscan: 10,
        measureElement:
            typeof window !== 'undefined' && !navigator.userAgent.includes('Firefox')
                ? el => el?.getBoundingClientRect().height
                : undefined,
    });

    const renderSortArrow = (direction: string, fieldName: string, arrow: string) => {
        return (
            <Typography className={style.sortIcon} style={{ color: sortConfig.direction === direction && fieldName === changeField ? "rgba(0,0,0,0.85)" : "rgba(0,0,0,0.1)" }}>{arrow}</Typography>
        );
    }

    const renderSortingArrows = (fieldName: string) => {
        return (
            <Grid container className={`${globalStyles.flexColumn}`}>
                {renderSortArrow("ascending", fieldName, "▲")}
                {renderSortArrow("descending", fieldName, "▼")}
            </Grid>
        );
    }

    const shouldShowCheckbox = () => {
        return (hasContactManageAccess(apiRoutesPermissionAssignment, userDetail) && !isContactListMasterContactList && !isContactListArchived)
    }

    return (
        <>
            <TableContainer className={`${style.contactTableContainer} ${globalStyles.relativePosition} ${globalStyles.overflowAuto}`} ref={tableContainerRef} sx={{ maxHeight: `${innerHeight}px !important`}}>
                <Table stickyHeader>
                    <TableHead className={style.contactTableHead}>
                        <TableRow className={`${globalStyles.displayFlexStyle} ${globalStyles.fullwidth}`}>
                            {shouldShowCheckbox() && 
                                <TableCell className={`${dashboardStyles.tableBodyHeadingFontStyle} ${style.checkBoxTableCell} ${style.checkBoxCellWidth}`}>
                                    <Checkbox
                                        checked={(filteredCampaignContactData && filteredCampaignContactData.length > 0 && filteredCampaignContactData[0].contact.length > 0) ? (selectContact.length === filteredCampaignContactData[0]?.contact.length ? true : false) : false}
                                        onChange={(e) => handleAllChanges()}
                                        inputProps={{ 'aria-label': 'controlled' }}
                                        disabled={(filteredCampaignContactData && filteredCampaignContactData.length === 0) ? true : false}
                                        color="secondary"
                                        className={style.checkBoxContact}
                                    />
                                </TableCell>
                            }

                            {campaignContactFieldData &&
                                sortField(campaignContactFieldData.contact_field_data).map((field, ind) => (

                                    <TableCell
                                        className={`${dashboardStyles.tableBodyHeadingFontStyle} ${globalStyles.clickable} ${style.fieldCellWidth}`}
                                        key={ind}
                                        onMouseEnter={() => { if (!field.flag_default && !(breakPoints.flagSmallScreen) && !isContactListMasterContactList) setHoveredHeader(field.contact_field_id) }}
                                        onMouseLeave={() => { if (!field.flag_default && !(breakPoints.flagSmallScreen && !isContactListMasterContactList)) setHoveredHeader(null) }}
                                        onClick={() => { sortData(field.contact_field_name) }}
                                    >
                                        <Grid container className={`${globalStyles.alignItemCenter} ${globalStyles.gapOne} ${globalStyles.fullwidth}`} flexWrap={"nowrap"}>
                                            <Grid item>
                                                {field.contact_field_name}
                                            </Grid>
                                            <Grid item> 
                                                {renderSortingArrows(field.contact_field_name)}
                                            </Grid>
                                            <Grid item>
                                                {((hoveredHeader === field.contact_field_id || (!field.flag_default && breakPoints.flagSmallScreen)) && !isContactListMasterContactList && !isContactListArchived) &&
                                                    <>
                                                        <MoreVertIcon className={style.headerEditIcon} onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleMenuOpen(e, field?.contact_field_id); }} />
                                                        <GlobalMenu
                                                            anchorEl={anchorEl}
                                                            handleMenuClose={handleMenuClose}
                                                            menuItemData={menuFieldItem}
                                                            open={field?.contact_field_id === currentRowIndex}
                                                            additionalField={field}
                                                        />
                                                    </>
                                                }
                                            </Grid>
                                        </Grid>
                                    </TableCell>
                                ))
                            }
                            <TableCell className={`${dashboardStyles.tableBodyHeadingFontStyle} ${style.tagCellWidth}`}>Tags</TableCell>
                            <TableCell className={`${dashboardStyles.tableBodyHeadingFontStyle} ${globalStyles.clickable} ${style.fieldCellWidth}`} onClick={() => sortData("Created At")}>
                                <Grid container className={`${globalStyles.alignItemCenter} ${globalStyles.gapOne} ${globalStyles.fullwidth}`} flexWrap={"nowrap"}>
                                    <Grid item>
                                        Created At
                                    </Grid>
                                    <Grid item>
                                        {renderSortingArrows("Created At")}
                                    </Grid>
                                </Grid>
                            </TableCell>
                            {hasContactManageAccess(apiRoutesPermissionAssignment, userDetail) && 
                                <TableCell className={`${dashboardStyles.tableBodyHeadingFontStyle} ${style.lastColumnSticky} ${style.checkBoxCellWidth} ${style.tertiaryBgColor}`}></TableCell>
                            }
                        </TableRow>
                    </TableHead>
                    <TableBody
                        sx={{
                            display: virtualizer.getVirtualItems().length == 0 ? 'contents' : 'block',
                            height: virtualizer.getVirtualItems().length == 0 ? 'auto' : `${virtualizer.getTotalSize()}px`,
                            position: virtualizer.getVirtualItems().length == 0 ? 'static' : 'relative',
                        }}
                    >
                        {virtualizer.getVirtualItems().length > 0
                            ?
                                virtualizer.getVirtualItems().map(virtualRow => {
                                    const data = getFilteredContacts[virtualRow.index];
                                    if (!data) return null;

                                    return (
                                        <TableRow
                                            key={data.contact_id}
                                            data-index={virtualRow.index}
                                            ref={node => virtualizer.measureElement(node)}
                                            className={`${globalStyles.absolutePosition} ${globalStyles.displayFlexStyle} ${globalStyles.fullwidth}`}
                                            style={{
                                                transform: `translateY(${virtualRow.start}px)`,
                                            }}
                                        >
                                            <MemoizedContactTableRow
                                                data={data}
                                                index={virtualRow.index}
                                                userTimezone={userDetail.timezone_hour}
                                                campaignContactFieldData={campaignContactFieldData}
                                                shouldShowCheckbox={shouldShowCheckbox}
                                                anchorEl={anchorEl}
                                                currentRowIndex={currentRowIndex}
                                                menuItem={menuItem}
                                                isContactListArchived={isContactListArchived}
                                                handleSelectContact={handleSelectContact}
                                                handleMenuOpen={handleMenuOpen}
                                                handleMenuClose={handleMenuClose}
                                                sortField={sortField}
                                                isMenuOpen={data.contact_id == currentRowIndex}
                                                isCheckBoxChecked={selectContact.find(contact => contact.contact_id == data.contact_id) ? true : false}
                                            />
                                        </TableRow>
                                    );
                                })
                            : 
                            <TableRow className={`${dashboardStyles.cellRow}`}>
                                <TableCell colSpan={12} className={globalStyles.textCenter}>
                                    No Data Available
                                </TableCell>
                            </TableRow>
                        }
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    )
}

export default ContactTable
